import type { RouteLocationNormalized } from 'vue-router'
import { hasAnyRole, session } from './session'

const salonOps = ['Salon Owner', 'Salon Manager']
const frontDesk = ['Salon Owner', 'Salon Manager', 'Salon Receptionist']
const staff = ['Salon Staff']
const customer = ['Customer']

export interface NavItem {
  label: string
  route: string
  icon: string
  roles: string[]
  section: string
  featureKey?: string
  /**
   * Boot feature area. Hidden when `featureAllowed` is false for it.
   * A list means every area must be allowed.
   */
  feature?: string | string[]
  /** Count badge shown next to the label (value comes from `leaveBadge`). */
  badge?: 'leaveOpen'
  tourId?: string
}

export interface NavSection {
  key: string
  label: string
  items: NavItem[]
}

/**
 * Product navigation from docs/port/contracts/screen-catalog.md.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    route: '/dashboard',
    icon: 'lucide-home',
    roles: salonOps,
    section: 'main',
  },
  {
    label: 'Appointments',
    route: '/appointments',
    icon: 'lucide-calendar',
    roles: frontDesk,
    section: 'main',
    feature: 'appointments',
  },
  {
    label: 'Front desk',
    route: '/front-desk',
    icon: 'lucide-clipboard-list',
    roles: frontDesk,
    section: 'main',
    feature: 'appointments',
  },
  {
    label: 'POS',
    route: '/pos',
    icon: 'lucide-banknote',
    roles: frontDesk,
    section: 'main',
    feature: 'pos',
  },
  {
    label: 'Customers',
    route: '/customers',
    icon: 'lucide-users',
    roles: frontDesk,
    section: 'main',
    feature: 'customers',
  },
  {
    label: 'Campaigns',
    route: '/campaigns',
    icon: 'lucide-send',
    roles: ['Salon Owner', 'Salon Manager'],
    section: 'main',
  },
  {
    label: 'Staff',
    route: '/staff',
    icon: 'lucide-user-cog',
    roles: salonOps,
    section: 'main',
  },
  {
    label: 'Staff access',
    route: '/staff/access',
    icon: 'lucide-shield-check',
    roles: salonOps,
    section: 'main',
  },
  {
    label: 'Commissions',
    route: '/commissions',
    icon: 'lucide-percent',
    roles: frontDesk,
    section: 'main',
    feature: 'commissions',
  },
  {
    label: 'Leave requests',
    route: '/leave',
    icon: 'lucide-calendar-clock',
    roles: salonOps,
    section: 'main',
    badge: 'leaveOpen',
  },
  {
    label: 'Inventory',
    route: '/inventory',
    icon: 'lucide-package',
    roles: salonOps,
    section: 'ops',
    feature: 'inventory',
  },
  {
    label: 'Stores',
    route: '/inventory/stores',
    icon: 'lucide-warehouse',
    roles: salonOps,
    section: 'ops',
    feature: 'inventory',
  },
  {
    label: 'Services',
    route: '/services',
    icon: 'lucide-scissors',
    roles: salonOps,
    section: 'ops',
  },
  {
    label: 'Price list',
    route: '/services/prices',
    icon: 'lucide-tag',
    roles: [...salonOps, 'Salon Receptionist'],
    section: 'ops',
  },
  {
    label: 'Products',
    route: '/products',
    icon: 'lucide-shopping-bag',
    roles: salonOps,
    section: 'ops',
  },
  {
    label: 'Shop orders',
    route: '/shop/orders',
    icon: 'lucide-shopping-bag',
    roles: frontDesk,
    section: 'ops',
  },
  {
    label: 'Branches',
    route: '/branches',
    icon: 'lucide-store',
    roles: salonOps,
    section: 'ops',
  },
  {
    label: 'Settings',
    route: '/settings',
    icon: 'lucide-settings',
    roles: salonOps,
    section: 'ops',
    feature: 'settings',
  },
  {
    label: 'Setup guide',
    route: '/setup',
    icon: 'lucide-list-checks',
    roles: ['Salon Owner'],
    section: 'ops',
  },
  {
    label: 'Finance overview',
    route: '/finance',
    icon: 'lucide-landmark',
    roles: salonOps,
    section: 'finance',
    feature: 'reports',
  },
  {
    label: 'Invoices',
    route: '/finance/invoices',
    icon: 'lucide-receipt',
    roles: salonOps,
    section: 'finance',
  },
  {
    label: 'Expenses',
    route: '/finance/expenses',
    icon: 'lucide-wallet',
    roles: frontDesk,
    section: 'finance',
    feature: 'finance',
  },
  {
    label: 'Bills',
    route: '/finance/bills',
    icon: 'lucide-receipt',
    roles: frontDesk,
    section: 'finance',
    feature: 'finance',
  },
  {
    label: 'Cash-up',
    route: '/finance/cash-up',
    icon: 'lucide-coins',
    roles: frontDesk,
    section: 'finance',
    feature: 'finance',
  },
  {
    label: 'Commission payouts',
    route: '/finance/payouts',
    icon: 'lucide-hand-coins',
    roles: frontDesk,
    section: 'finance',
    feature: ['finance', 'payouts'],
  },
  {
    label: 'Profit and loss',
    route: '/finance/reports',
    icon: 'lucide-chart-pie',
    roles: frontDesk,
    section: 'finance',
    feature: 'finance',
  },
  {
    label: 'Referrals',
    route: '/referrals',
    icon: 'lucide-handshake',
    roles: salonOps,
    section: 'finance',
  },
  {
    label: 'Sales report',
    route: '/reports/sales',
    icon: 'lucide-chart-bar',
    roles: salonOps,
    section: 'finance',
    feature: 'reports',
  },
  {
    label: 'Daily closeout',
    route: '/reports/closeout',
    icon: 'lucide-calendar-check',
    roles: salonOps,
    section: 'finance',
    feature: ['reports', 'finance'],
  },
  {
    label: 'Reconciliation',
    route: '/reports/reconciliation',
    icon: 'lucide-scale',
    roles: salonOps,
    section: 'finance',
    feature: ['reports', 'finance'],
  },
  {
    label: 'Platform fee',
    route: '/settings/platform-fee',
    icon: 'lucide-badge-percent',
    roles: ['Salon Owner'],
    section: 'finance',
  },
  {
    label: 'Payment methods',
    route: '/settings/payments',
    icon: 'lucide-credit-card',
    roles: salonOps,
    section: 'finance',
  },
  {
    label: 'M-Pesa',
    route: '/finance/mpesa',
    icon: 'lucide-smartphone',
    roles: frontDesk,
    section: 'finance',
    feature: 'finance',
  },
  {
    label: 'My day',
    route: '/my/day',
    icon: 'lucide-sun',
    roles: staff,
    section: 'staff',
  },
  {
    label: 'My commissions',
    route: '/my/commissions',
    icon: 'lucide-percent',
    roles: staff,
    section: 'staff',
  },
  {
    label: 'Time off',
    route: '/my/time-off',
    icon: 'lucide-calendar-off',
    roles: staff,
    section: 'staff',
  },
  {
    label: 'My profile',
    route: '/my/profile',
    icon: 'lucide-user',
    roles: staff,
    section: 'staff',
  },
  {
    label: 'Book',
    route: '/portal/book',
    icon: 'lucide-calendar-plus',
    roles: customer,
    section: 'portal',
  },
  {
    label: 'My appointments',
    route: '/portal/appointments',
    icon: 'lucide-calendar-check',
    roles: customer,
    section: 'portal',
  },
  {
    label: 'Receipts',
    route: '/portal/receipts',
    icon: 'lucide-receipt',
    roles: customer,
    section: 'portal',
  },
  {
    label: 'Listing reports',
    route: '/platform/reports',
    icon: 'lucide-flag',
    roles: ['Glamora Platform Admin'],
    section: 'platform',
  },
  {
    label: 'Referral flags',
    route: '/platform/referral-flags',
    icon: 'lucide-flag',
    roles: ['Glamora Platform Admin'],
    section: 'platform',
  },
  {
    label: 'Platform settings',
    route: '/platform/settings',
    icon: 'lucide-settings',
    roles: ['Glamora Platform Admin'],
    section: 'platform',
  },
]

const SECTION_LABELS: Record<string, string> = {
  main: 'Salon',
  ops: 'Operations',
  finance: 'Finance',
  staff: 'My workspace',
  portal: 'Client portal',
  platform: 'Platform',
}

/**
 * True unless this login's boot explicitly switches the area off.
 * A missing `features` object, or a missing key, leaves the screen available.
 */
export function featureAllowed(area: string): boolean {
  const features = session.boot?.features
  if (!features) return true
  return features[area] !== false
}

function featureListAllowed(feature: unknown): boolean {
  if (feature == null || feature === '') return true
  const areas = Array.isArray(feature) ? feature : [feature]
  return areas.every((area) => typeof area === 'string' && featureAllowed(area))
}

export function canAccessRoute(route: RouteLocationNormalized): boolean {
  if (!route) return false
  if (route.meta?.public) return true
  if (!session.boot?.authenticated) return false
  const routeRoles = route.meta?.roles as string[] | undefined
  if (routeRoles?.length && !hasAnyRole(routeRoles)) return false
  return featureListAllowed(route.meta?.feature)
}

export function navBySection(): NavSection[] {
  const items = NAV_ITEMS.filter(
    (item) => hasAnyRole(item.roles) && featureListAllowed(item.feature),
  )
  const sections: NavSection[] = []
  const seen = new Map<string, NavItem[]>()

  for (const item of items) {
    const key = item.section ?? 'main'
    if (!seen.has(key)) {
      seen.set(key, [])
      sections.push({
        key,
        label: SECTION_LABELS[key] ?? key,
        items: seen.get(key)!,
      })
    }
    seen.get(key)!.push(item)
  }
  return sections
}

/**
 * Generic permission check. Currently maps permission strings to role arrays.
 * Extend as needed when proper Frappe permissions are wired up.
 */
const PERM_ROLES: Record<string, string[]> = {
  'admin.company.view': ['Salon Owner', 'Glamora Platform Admin'],
  'admin.company.write': ['Salon Owner', 'Glamora Platform Admin'],
  'admin.branch.manage': [
    'Salon Owner',
    'Salon Manager',
    'Glamora Platform Admin',
  ],
}

export function can(permission: string): boolean {
  const required = PERM_ROLES[permission]
  if (!required) return false
  return hasAnyRole(required)
}
